import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import PageHeader from '../../components/PageHeader';
import DataState, { TableSkeleton } from '../../components/DataState';
import SourceStatusBanner from './modernwash/SourceStatusBanner';
import { useModernwashResource } from './modernwash/useModernwashResource';
import { fetchModernwashVehicles } from '../../api/modernwash';
import { colors } from '../../design-system/tokens';
import { pick, formatTime } from './modernwash/format';

/**
 * 차량 관리 — ModernWash 원천의 회원↔차량 (read-only, 2모드).
 *
 * v1 이 회원 목록과 회원별 차량을 분리 제공하므로(전체 N+1 회피) 화면도 두 단계로 나눈다:
 *   1) 회원 목록 (data.mode==='members', 페이지네이션) → 각 행 "차량 보기"
 *   2) 선택 회원의 차량 (data.mode==='vehicles', memberId 쿼리) → "회원 목록" 으로 복귀
 * 계약: backend dto VehiclesData / MemberSummaryDto / VehicleDto.
 */
const carLabel = (row) => {
  const parts = [
    pick(row, ['maker']),
    pick(row, ['model']),
  ].filter(Boolean);
  const color = pick(row, ['color']);
  const base = parts.join(' ');
  if (base && color) return `${base} · ${color}`;
  return base || color || '-';
};

const Vehicles = () => {
  // memberId 가 있으면 차량 모드, 없으면 회원 목록 모드
  const [selected, setSelected] = useState(null); // { id, nickname } | null
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);

  const params = useMemo(
    () => (selected ? { memberId: selected.id } : { page: page + 1, limit }),
    [selected, page, limit],
  );
  const { loading, reload, source, data } = useModernwashResource(fetchModernwashVehicles, params);

  const inVehicleMode = Boolean(selected);
  const members = Array.isArray(data?.members) ? data.members : [];
  const vehicles = Array.isArray(data?.vehicles) ? data.vehicles : [];
  const total = Number.isFinite(data?.total) ? data.total : members.length;

  const goVehicles = (member) => setSelected({ id: member.id, nickname: member.nickname });
  const goMembers = () => setSelected(null);

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1 }}
      >
        <PageHeader
          title="차량 관리"
          description="ModernWash 회원과 등록 차량이에요. 회원을 고르면 해당 회원의 차량을 보여줍니다. (원천 읽기 전용)"
        />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
          {!loading && !source.blocking && source.fetchedAt && (
            <Typography variant="caption" sx={{ color: colors.text.muted, whiteSpace: 'nowrap' }}>
              동기화 {formatTime(source.fetchedAt)}
            </Typography>
          )}
          <Tooltip title="새로고침" arrow>
            <span>
              <IconButton onClick={reload} disabled={loading} aria-label="새로고침">
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {inVehicleMode && (
        <Box sx={{ mb: 1.5 }}>
          <Button
            size="small"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={goMembers}
            sx={{ color: colors.text.secondary }}
          >
            회원 목록
          </Button>
          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
            {selected?.nickname || '이름 미등록'} 님의 차량
          </Typography>
        </Box>
      )}

      {loading ? (
        <TableSkeleton rows={6} cols={inVehicleMode ? 4 : 4} />
      ) : (
        <>
          <SourceStatusBanner state={source} onRetry={reload} />
          {!source.blocking &&
            (inVehicleMode ? (
              vehicles.length > 0 ? (
                <Paper sx={{ p: 0, overflow: 'hidden' }}>
                  <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <Table size="small" sx={{ minWidth: 640 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>번호판</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>차량</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>대표</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>멤버십</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vehicles.map((row, index) => {
                          const isPrimary = pick(row, ['isPrimary', 'primary']);
                          const membership = pick(row, ['linkedMembershipName']);
                          return (
                            <TableRow key={pick(row, ['id', '_id', 'licensePlate'], `vehicle-${index}`)} hover>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                                  <DirectionsCarRoundedIcon fontSize="small" sx={{ color: colors.text.muted }} />
                                  <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                    {pick(row, ['licensePlate', 'plate'], '-')}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                                  {carLabel(row)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {isPrimary ? (
                                  <Chip
                                    size="small"
                                    label="대표"
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      fontWeight: 700,
                                      bgcolor: colors.brand.primarySoft,
                                      color: colors.brand.primary,
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2" sx={{ color: colors.text.muted }}>
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {membership ? (
                                  <Chip
                                    size="small"
                                    label={membership}
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      fontWeight: 700,
                                      bgcolor: colors.status.success.bg,
                                      color: colors.status.success.fg,
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2" sx={{ color: colors.text.muted }}>
                                    없음
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : (
                <DataState loading={false} empty emptyText="이 회원은 등록된 차량이 없어요." />
              )
            ) : members.length > 0 ? (
              <Paper sx={{ p: 0, overflow: 'hidden' }}>
                <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>회원</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                          차량
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {members.map((row, index) => (
                        <TableRow key={pick(row, ['id', '_id'], `member-${index}`)} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {pick(row, ['nickname'], '이름 미등록')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DirectionsCarRoundedIcon fontSize="small" />}
                              onClick={() => goVehicles(row)}
                              aria-label={`${pick(row, ['nickname'], '회원')} 차량 보기`}
                              sx={{ minWidth: 92, px: 1 }}
                            >
                              차량 보기
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(event, next) => setPage(next)}
                  rowsPerPage={limit}
                  onRowsPerPageChange={(event) => {
                    setLimit(Number(event.target.value));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  labelRowsPerPage="페이지당"
                />
              </Paper>
            ) : (
              <DataState loading={false} empty emptyText="아직 연결된 회원이 없어요." />
            ))}
        </>
      )}
    </Box>
  );
};

export default Vehicles;
